// @spec AI-STR-027
// TanStack Router route for AI usage monitoring dashboard

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BarChart3, ShieldAlert } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Card, CardContent } from '@/components/ui/card';
import {
  UsageBreakdownTable,
  UsageExportButton,
  UsageFilters,
  UsageSummaryCards,
  UsageTrendChart,
  useUsageByFeature,
  useUsageByUser,
  useUsageSummary,
  useUsageTimeline,
} from '@/features/ai/usage';
import type { UsageMetric, UsagePeriod, UsageSortBy } from '@/features/ai/usage';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ai/usage')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.aiUsage'),
  }),
  component: AIUsagePage,
});

function AIUsagePage() {
  const navigate = useNavigate();
  const { isAdmin, isLoading: isLoadingRole } = useOrgRole();

  const [period, setPeriod] = useState<UsagePeriod>('month');
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null);
  const [metric, setMetric] = useState<UsageMetric>('tokens');
  const [breakdownMode, setBreakdownMode] = useState<'user' | 'feature'>('user');
  const [sortBy, setSortBy] = useState<UsageSortBy>('tokens');

  const queryOptions = { period, dateRange };

  const { data: summary, isLoading: summaryLoading } = useUsageSummary(queryOptions);
  const { data: byUserData, isLoading: byUserLoading } = useUsageByUser({
    ...queryOptions,
    sortBy,
  });
  const { data: byFeatureData, isLoading: byFeatureLoading } = useUsageByFeature(queryOptions);
  const { data: timelineData, isLoading: timelineLoading } = useUsageTimeline(queryOptions);

  useEffect(() => {
    if (!isLoadingRole && !isAdmin) {
      void navigate({ to: '/ai' });
    }
  }, [isLoadingRole, isAdmin, navigate]);

  if (!isLoadingRole && !isAdmin) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-5xl">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <ShieldAlert className="h-12 w-12 text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
              <p className="text-muted-foreground text-sm mb-4">
                You do not have permission to view AI usage data. This page is restricted to
                Organization Admins.
              </p>
              <button
                onClick={() => void navigate({ to: '/' })}
                className="text-primary hover:underline text-sm"
              >
                Return to Dashboard
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <BarChart3 className="h-5 w-5" />
            </div>
            <h1 className="text-2xl font-semibold">AI Usage</h1>
          </div>
          <p className="text-muted-foreground">
            Monitor AI token consumption, costs, and usage patterns across your organization.
          </p>
          {/*
            TODO(WP4-B): Render <GroupScopePill /> once the AI usage summary
            / timeline / breakdown queries expose rollup scope metadata. The
            `ai-usage` query-key prefix is already registered in
            `ADDITIONAL_DASHBOARD_PREFIXES` for invalidation, but the backend
            responses do not yet include `RollupScopeMixin` fields. See
            `docs/subsidiary-refactor/MIGRATION-GUIDE.md`.
          */}
        </div>

        {/* Filters bar */}
        <div className="flex items-center justify-between">
          <UsageFilters
            period={period}
            onPeriodChange={setPeriod}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />
          <UsageExportButton period={period} dateRange={dateRange} />
        </div>

        {/* Summary cards */}
        <UsageSummaryCards summary={summary} isLoading={summaryLoading} />

        {/* Trend chart */}
        <UsageTrendChart
          data={timelineData?.dataPoints}
          isLoading={timelineLoading}
          metric={metric}
          onMetricChange={setMetric}
        />

        {/* Breakdown table */}
        <UsageBreakdownTable
          mode={breakdownMode}
          userData={byUserData?.users}
          featureData={byFeatureData?.features}
          isLoading={breakdownMode === 'user' ? byUserLoading : byFeatureLoading}
          sortBy={sortBy}
          onSortChange={(s) => { setSortBy(s as UsageSortBy); }}
          onModeChange={setBreakdownMode}
        />
      </div>
    </div>
  );
}
